// /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/animations/AnimatedWindow.tsx

import React, { useEffect, useState, useRef } from 'react';

interface AnimatedWindowProps {
  className?: string;
  sunriseHour?: number;
  sunsetHour?: number;
}

const AnimatedWindow: React.FC<AnimatedWindowProps> = ({
  className = '',
  sunriseHour = 6,
  sunsetHour = 20,
}) => {
  const [isDay, setIsDay] = useState(true);
  const temperatureRef = useRef<number | null>(null);

  useEffect(() => {
    const logInfo = () => {
      const now = new Date();
      
      const berlinDate = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(now).replace(/-/g, '/');
      
      const berlinTime = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Berlin',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(now);

      const currentHour = parseInt(berlinTime.split(':')[0], 10);
      const isDaytime = currentHour >= sunriseHour && currentHour < sunsetHour;
      setIsDay(isDaytime);

      const tempStr = temperatureRef.current !== null ? ` | 🌡️ ${temperatureRef.current}°C` : '';
      console.log(`🕐 Berlin: ${berlinDate} ${berlinTime} | ${isDaytime ? '☀️ Day' : '🌙 Night'}${tempStr}`);
    };

    const fetchWeather = async () => {
      try {
        const response = await fetch('https://wttr.in/Berlin?format=%t');
        const text = await response.text();
        const temp = parseInt(text.replace(/[^-\d]/g, ''), 10);
        if (!isNaN(temp)) {
          temperatureRef.current = temp;
          logInfo();
        }
      } catch (error) {
        logInfo();
      }
    };

    fetchWeather();
  }, [sunriseHour, sunsetHour]);

  return (
    <div
      className={`relative w-full h-full overflow-hidden ${className}`}
      style={{
        backgroundColor: isDay ? '#89a4bf' : '#25313f',
        transition: 'background-color 2s ease-in-out',
      }}
    >
      <div 
        className="absolute inset-0"
        style={{
          background: isDay 
            ? 'linear-gradient(180deg, #89a4bf 0%, #a0b4c8 40%, #bcc8d4 100%)'
            : 'linear-gradient(180deg, #2a3645 0%, #38485b 40%, #42556a 70%, #3a495b 100%)',
          transition: 'opacity 2s ease-in-out',
        }}
      />

      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(1100px 700px at 50% 50%, rgba(200, 220, 245, 0.08), transparent 60%)',
          backgroundSize: '250% 100%',
          animation: 'sky-shift 40s linear infinite',
          mixBlendMode: 'screen',
          opacity: isDay ? 0.6 : 0.3,
          transition: 'opacity 2s ease-in-out',
        }}
      />

      <div
        className="animated-window-container"
        style={{
          position: 'absolute',
          right: '8%',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '35%',
          aspectRatio: '1 / 1',
          maxWidth: '480px',
        }}
      >
        <img
          src="/images/Header_02.png"
          alt="Night View"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            filter: 'drop-shadow(0 20px 60px rgba(0, 0, 0, 0.55))',
            opacity: isDay ? 0 : 1,
            transition: 'opacity 2s ease-in-out',
          }}
        />

        <img
          src="/images/Header_01.png"
          alt="Day View"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            filter: 'drop-shadow(0 20px 60px rgba(0, 0, 0, 0.4))',
            opacity: isDay ? 1 : 0,
            transition: 'opacity 2s ease-in-out',
          }}
        />
      </div>

      <div
        style={{
          position: 'absolute',
          right: '8%',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '35%',
          aspectRatio: '1 / 1',
          maxWidth: '480px',
          pointerEvents: 'none',
          filter: 'blur(80px)',
          background: isDay
            ? 'radial-gradient(circle, rgba(180, 210, 245, 0.25) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(90, 130, 200, 0.28) 0%, transparent 70%)',
          opacity: isDay ? 0.6 : 0.7,
          transition: 'background 2s ease-in-out, opacity 2s ease-in-out',
        }}
      />

      <style>{`
        @keyframes sky-shift {
          0%   { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }

        @keyframes window-intro {
          0%   { opacity: 0; transform: translateY(-50%) translateX(90px) scale(0.96); }
          100% { opacity: 1; transform: translateY(-50%) translateX(0) scale(1); }
        }

        .animated-window-container {
          animation: window-intro 900ms ease-out forwards;
        }

        @media (max-width: 768px) {
          .animated-window-container,
          .animated-window-container + div {
            width: 50% !important;
            max-width: 380px !important;
            right: 5% !important;
          }
        }

        @media (max-width: 480px) {
          .animated-window-container,
          .animated-window-container + div {
            width: 60% !important;
            max-width: 280px !important;
            right: 3% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AnimatedWindow;
